// Intl is the only thing in the browser that holds a zone's rules; everything here is a way of asking it.

// ICU's canonical name is whatever a zone was called when it was added, so the list still says Calcutta and Kiev.
// Every name on the right formats as readily as the one on the left, and is the one somebody would search for.
const RENAMED: Record<string, string> = {
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "America/Godthab": "America/Nuuk",
  "America/Indianapolis": "America/Indiana/Indianapolis",
  "America/Louisville": "America/Kentucky/Louisville",
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Rangoon": "Asia/Yangon",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Atlantic/Faeroe": "Atlantic/Faroe",
  "Europe/Kiev": "Europe/Kyiv",
  "Pacific/Enderbury": "Pacific/Kanton",
  "Pacific/Ponape": "Pacific/Pohnpei",
  "Pacific/Truk": "Pacific/Chuuk",
};

export const LOCAL_ZONE = currentName(Intl.DateTimeFormat().resolvedOptions().timeZone);

// Intl lists the canonical zones and leaves UTC out of them, though it is the one zone a picker cannot do without.
export const TIME_ZONES = zoneList();

function zoneList(): string[] {
  const named = new Set(Intl.supportedValuesOf("timeZone").map(currentName));
  named.add(LOCAL_ZONE);
  named.delete("UTC");
  return ["UTC", ...[...named].sort()];
}

function currentName(timeZone: string): string {
  return RENAMED[timeZone] ?? timeZone;
}

export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export interface ZoneClock extends WallClock {
  timeZone: string;
  offsetMs: number;
  millisecond: number;
  // Sunday is 0, as everywhere else in JavaScript; the ISO week date is the one place that has to renumber it.
  weekday: number;
}

export function zoneClock(date: Date, timeZone: string): ZoneClock {
  const offsetMs = zoneOffsetMs(date, timeZone);
  const shifted = new Date(date.getTime() + offsetMs);
  return {
    timeZone,
    offsetMs,
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
    millisecond: shifted.getUTCMilliseconds(),
    weekday: shifted.getUTCDay(),
  };
}

const DAY_MS = 86400000;

// The other direction: a wall clock is a reading, and which instant it names depends on the zone's rules that day.
// An hour the clocks hand back is read twice, and this takes the first of the two, as a crontab and mktime both do.
// An hour they take away is read never, and the instant handed back is where the clock went instead — later than
// what was asked for, which is what lets a caller notice by reading it back and finding a different time.
export function zoneInstant(wall: WallClock, timeZone: string): number {
  // Built through the setter rather than Date.UTC, which reads the first hundred years as the nineteen hundreds.
  const start = utcDate(wall.year, wall.month, wall.day);
  start.setUTCHours(wall.hour, wall.minute, wall.second, 0);
  const guess = start.getTime();
  // A transition sits between these two, so one of the offsets either side of it is the one that applies.
  const early = guess - zoneOffsetMs(new Date(guess - DAY_MS), timeZone);
  const late = guess - zoneOffsetMs(new Date(guess + DAY_MS), timeZone);
  if (readsAs(early, wall, timeZone)) return early;
  if (readsAs(late, wall, timeZone)) return late;
  return early;
}

const WALL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})$/;

// A picker hands back a wall clock as text, and reading it is putting a zone to it: whose clock it was tells which.
export function wallDate(text: string, timeZone: string): Date | null {
  const match = WALL_PATTERN.exec(text);
  if (!match) return null;
  const wall: WallClock = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6]),
  };
  return new Date(zoneInstant(wall, timeZone));
}

// The other direction again: what a panel is handed to open on, spelled the way the one above reads it back.
export function wallText(wall: WallClock): string {
  const date = `${String(wall.year).padStart(4, "0")}-${pad(wall.month)}-${pad(wall.day)}`;
  return `${date} ${pad(wall.hour)}:${pad(wall.minute)}:${pad(wall.second)}`;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function readsAs(ms: number, wall: WallClock, timeZone: string): boolean {
  return wallKey(zoneClock(new Date(ms), timeZone)) === wallKey(wall);
}

export function wallKey(wall: WallClock): number {
  return ((((wall.year * 12 + wall.month) * 31 + wall.day) * 24 + wall.hour) * 60 + wall.minute) * 60 + wall.second;
}

// Reading the zone's wall clock back as UTC is what turns its rules into an offset.
function zoneOffsetMs(date: Date, timeZone: string): number {
  const parts: Record<string, string> = {};
  for (const part of offsetFormatter(timeZone).formatToParts(date)) parts[part.type] = part.value;
  const wall = utcDate(Number(parts.year), Number(parts.month), Number(parts.day));
  wall.setUTCHours(Number(parts.hour), Number(parts.minute), Number(parts.second), 0);
  // The wall clock is only formatted to the second, so the instant is dropped to one before the two are compared.
  return wall.getTime() - Math.floor(date.getTime() / 1000) * 1000;
}

// Date's constructor reads years 0 through 99 as the 1900s, and only the setter takes them as themselves.
export function utcDate(year: number, month: number, day: number): Date {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

// Building one of these costs more than every format it is then asked for, and a page holds a handful of zones.
const OFFSET_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function offsetFormatter(timeZone: string): Intl.DateTimeFormat {
  return formatterFor(OFFSET_FORMATTERS, timeZone, {
    locale: "en-US",
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function formatterFor(
  cache: Map<string, Intl.DateTimeFormat>,
  timeZone: string,
  { locale, ...options }: Intl.DateTimeFormatOptions & { locale?: string },
): Intl.DateTimeFormat {
  let formatter = cache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, { ...options, timeZone });
    cache.set(timeZone, formatter);
  }
  return formatter;
}
